import React from 'react';
import {
    Typography,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Box,
    IconButton,
    Chip,
    Skeleton,
    Stack
} from '@mui/material';
import dayjs from 'dayjs';
import DownloadIcon from '@mui/icons-material/Download';
import { getTranslation } from 'common';
import { useTranslations } from 'hooks';

interface Props {
    allSubscriptions: {
        organization_name: string;
        invoices?: {
            amount: string;
            currency: string;
            invoice_date: string;
            paid_date?: string | null;
            pdf_url?: string;
            status: string;
        }[];
    }[];
    loading: boolean
}

const InvoiceHistory: React.FC<Props> = ({ allSubscriptions, loading }) => {
    const totalInvoices = allSubscriptions.reduce((acc, sub) => acc + (sub.invoices?.length || 0), 0);
   const translations = useTranslations();
    const getInvoiceStatusLabel = (status: string) => {
        if (status === "paid") {
            return getTranslation(translations, "generic.paid");
        }
        if (status === "draft") {
            return getTranslation(translations, "generic.draft");
        }
        return status;
    };

    return (
        <Box mt={4} mb={4}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
                {getTranslation(translations, "billing.invoice_history")}
            </Typography>
            {loading ? <Stack spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={50}
                  width={"100%"}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Stack> :
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{getTranslation(translations, "placeholders.organization.name.label")}</TableCell>
                            <TableCell>{getTranslation(translations, "subscription_invoice_emission")}</TableCell>
                            <TableCell>{getTranslation(translations, "subscription_payment_date")}</TableCell>
                            <TableCell>{getTranslation(translations, "generic.amount")}</TableCell>
                            <TableCell>{getTranslation(translations, "generic.status")}</TableCell>
                            <TableCell>{getTranslation(translations, "settings.download.label")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allSubscriptions.map((sub, subIndex) =>
                            (sub.invoices ?? []).map((inv, invIndex) => (
                                <TableRow key={`${subIndex}-${invIndex}`}>
                                    <TableCell>{sub.organization_name}</TableCell>
                                    <TableCell>{dayjs(inv.invoice_date).format("DD/MM/YYYY")}</TableCell>
                                    <TableCell>{inv.paid_date ? dayjs(inv.paid_date).format("DD/MM/YYYY") : "N/A"}</TableCell>
                                    <TableCell>{inv.amount} {inv.currency.toUpperCase()}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getInvoiceStatusLabel(inv.status)}
                                            color={inv.status === "paid" ? "success" : "warning"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {inv.pdf_url && (
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    const link = document.createElement("a");
                                                    link.href = inv.pdf_url!;
                                                    link.setAttribute("download", "fattura.pdf");
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    link.remove();
                                                }}
                                            >
                                                <DownloadIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                        {totalInvoices === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        {getTranslation(translations, "billing.no_invoices")}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>}
        </Box>
    );
};

export default InvoiceHistory;
